import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { I18nManager, Platform } from "react-native";
import {
  t as translate,
  isRTL,
  LOCALES,
  type SupportedLocale,
  type TranslationKeys,
} from "@chinese-vendor/i18n";
import { localizationSyncService, type LocalizationOverrides } from "@/lib/i18n/localizationSync";
import { registerLocaleAccessor } from "@/lib/api/client";
import { localizationApi } from "@/lib/api/localization";
import { localeStorage } from "@/lib/storage/localeStorage";

interface I18nContextValue {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  t: (key: TranslationKeys, interpolations?: Record<string, string | number>) => string;
  isRTL: boolean;
  availableLocales: { locale: SupportedLocale; label: string }[];
  /** True once the persisted locale preference has been read from storage. */
  localeHydrated: boolean;
  /** True if the user has previously saved a locale preference. */
  hasStoredLocale: boolean;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const DEFAULT_LOCALE: SupportedLocale = "en";

function normalizeOverrides(
  overrides: LocalizationOverrides | null | undefined
): LocalizationOverrides {
  return overrides && typeof overrides === "object" ? overrides : {};
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(DEFAULT_LOCALE);
  const [remoteOverrides, setRemoteOverrides] = useState<LocalizationOverrides>({});
  const [localeHydrated, setLocaleHydrated] = useState(false);
  const [hasStoredLocale, setHasStoredLocale] = useState(false);

  // Keep a stable ref so the locale accessor always returns the latest value
  // without needing to re-register on every locale change.
  const localeRef = useRef<SupportedLocale>(DEFAULT_LOCALE);
  localeRef.current = locale;

  // ── Register locale accessor (once on mount) ───────────────────────────────
  useEffect(() => {
    registerLocaleAccessor(() => localeRef.current);
  }, []);

  // ── Register the fetchBundle callback (once on mount) ─────────────────────
  useEffect(() => {
    localizationSyncService.registerFetchBundle(async (loc: SupportedLocale) => {
      const bundle = await localizationApi.getBundle(loc);
      return normalizeOverrides(bundle);
    });
  }, []);

  // ── Subscribe to override updates from the sync service ────────────────────
  useEffect(() => {
    // Apply any already-cached overrides for the current locale immediately.
    const existing = normalizeOverrides(localizationSyncService.getOverrides(locale));
    if (Object.keys(existing).length > 0) setRemoteOverrides(existing);

    const unsubscribe = localizationSyncService.subscribe((updatedLocale, overrides) => {
      if (updatedLocale === localeRef.current) {
        setRemoteOverrides(normalizeOverrides(overrides));
      }
    });

    return unsubscribe;
    // Re-subscribe when locale changes so we pick up the correct overrides.
  }, [locale]);

  const applyRTL = useCallback((newLocale: SupportedLocale) => {
    const shouldBeRTL = isRTL(newLocale);
    if (I18nManager.isRTL !== shouldBeRTL) {
      I18nManager.allowRTL(shouldBeRTL);
      I18nManager.forceRTL(shouldBeRTL);
    }
    if (Platform.OS === "web" && typeof document !== "undefined") {
      document.documentElement.dir = shouldBeRTL ? "rtl" : "ltr";
      document.documentElement.lang = newLocale;
    }
  }, []);

  const setLocale = useCallback(
    (newLocale: SupportedLocale) => {
      applyRTL(newLocale);
      setLocaleState(newLocale);
      localeStorage.set(newLocale);
      // Reset overrides when switching locale; the sync service will re-populate.
      setRemoteOverrides(normalizeOverrides(localizationSyncService.getOverrides(newLocale)));
    },
    [applyRTL]
  );

  // ── Restore persisted locale preference on mount ───────────────────────────
  useEffect(() => {
    localeStorage.get().then((stored) => {
      const resolved = stored && stored in LOCALES ? (stored as SupportedLocale) : DEFAULT_LOCALE;
      applyRTL(resolved);
      if (resolved !== DEFAULT_LOCALE) {
        setLocaleState(resolved);
      }
      setHasStoredLocale(!!stored);
      setLocaleHydrated(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tFn = useCallback(
    (key: TranslationKeys, interpolations?: Record<string, string | number>): string => {
      // Remote overrides (fetched from API) take precedence over bundled strings.
      const override = normalizeOverrides(remoteOverrides)[key as string];
      if (override !== undefined) {
        if (!interpolations) return override;
        // Use a function replacement to prevent special `$` sequences from
        // being interpreted by String.prototype.replace (e.g. currency values).
        return Object.entries(interpolations).reduce((s, [k, v]) => {
          const lit = String(v);
          return s.replace(new RegExp(`\\{${k}\\}`, "g"), () => lit);
        }, override);
      }
      return translate(key, locale, interpolations);
    },
    [locale, remoteOverrides]
  );

  const availableLocales = Object.entries(LOCALES).map(([loc, label]) => ({
    locale: loc as SupportedLocale,
    label,
  }));

  return (
    <I18nContext.Provider
      value={{
        locale,
        setLocale,
        t: tFn,
        isRTL: isRTL(locale),
        availableLocales,
        localeHydrated,
        hasStoredLocale,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
}
